import { Dispatch, SetStateAction, useEffect, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { RootState } from '../../../redux/store';
import Modal from '../../molecules/Modal/Modal';
import Text from '../../atoms/Text';
import React from 'react';
import { formatPrice } from '../../../shared/utils/general.util';
import Button from '../../atoms/Button';
import { getSubscriptionWithPaymentInfo } from '../../../services/suscription.service';
import { Token } from '../../../@types/Token.type';
import { ALERT_TYPE } from '../../molecules/Alert';
import { AlertPayload, showAlert } from '../../../redux/slices/alert.slice';
import { Barricade } from '@phosphor-icons/react';
import { ShoppingCart } from '../../../@types/ShoppingCart.type';
import ModalSelectCard from '../../../pages/shopping/components/organisms/ModalSelectCard';
import { SubscriptionStatusEnum } from '../../../@types/SubscriptionWithPaymentInfo.enum';
import { CircularProgress } from '@mui/material';
import { SubscriptionWithPaymentInfo } from '../../../@types/SubscriptionWithPaymentInfo.type';

interface Props {
	showModal: boolean;
	setShowModal: Dispatch<SetStateAction<any>>;
	onClose?: (isCompleted: boolean) => void;
	subscriptionId: number;
}

// Function to map specific error messages
const getSpecificErrorMessage = (failureReason: string): string | null => {
	const errorMap: { [key: string]: string } = {
		'Your card has insufficient funds.': 'No hay fondos suficientes en tu cuenta.',
		'Your card was declined.': 'Tu tarjeta fue declinada por el banco.',
		'Your card has expired.': 'Tu tarjeta está vencida.',
		'Your card number is incorrect.': 'El número de tu tarjeta es incorrecto.',
		'Your card\'s security code is incorrect.': 'El código de seguridad de tu tarjeta es incorrecto.',
		'Your card does not support this type of purchase.': 'Tu tarjeta no soporta este tipo de compra.',
		'Processing error': 'Error de procesamiento en el sistema de pagos.',
		'Authentication required': 'Se requiere autenticación adicional de tu banco.',
		'Card blocked': 'Tu tarjeta está bloqueada.',
		// Add more mappings as needed
	};

	return errorMap[failureReason] || null;
};

export type AssignOrgType = {
	org_code: string;
};

const ModalFailedRenewal: React.FC<Props> = ({
	showModal,
	setShowModal,
	subscriptionId,
	onClose,
}) => {
	const dispatch = useDispatch();
	const [isLoadingButton, setIsLoadingButton] = useState(false);
	const [subscriptionWithPaymentInfo, setSubscriptionWithPaymentInfo] =
		useState<SubscriptionWithPaymentInfo | null>(null);
	const [isRenewalCompleted, setIsRenewalCompleted] = useState(false);
	const [showModalPaymentFailed, setShowModalPaymentFailed] = useState(false);

	const [selectedCard, setSelectedCard] = useState<Token | null>(null);
	const [showSelectCard, setShowSelectCard] = useState(false);
	const [shoppingCartRenewal, setShoppingCartRenewal] =
		useState<ShoppingCart | null>(null);
	const [isLoading, setIsLoading] = useState(false);
	const { tokenSession } = useSelector((state: RootState) => state.auth);

	const handleCloseModal = () => {
		setIsRenewalCompleted(false);
		setShowModal(false);
		setSelectedCard(null);
		setIsLoadingButton(false);
		onClose?.(isRenewalCompleted);
	};
	const showAlertTop = ({ title, type, description }: AlertPayload) => {
		const alert = {
			type,
			title,
			description,
		};
		dispatch(showAlert(alert));
	};

	const onPaymentSuccess = (token: Token) => {
		setShowSelectCard(false);
		setSelectedCard(token);
		setIsRenewalCompleted(true);
		
		// Show success alert
		showAlertTop({
			title: '¡Renovación exitosa!',
			type: 'success',
			description: 'Tu suscripción de contabilidad ha sido renovada con éxito. Podrás continuar disfrutando de todos los servicios sin interrupciones.',
		});
		
		// Close modal after showing alert
		setTimeout(() => {
			handleCloseModal();
		}, 1500);
	};
	const onPaymentFailed = (token: Token) => {
		setShowSelectCard(false);
		setShowModalPaymentFailed(true);
		setSelectedCard(token);
	};

	const executeSubscriptionWithPaymentInfo = async () => {
		try {
			setIsLoading(true);
			const response = await getSubscriptionWithPaymentInfo(
				subscriptionId as number,
				tokenSession,
			);
			if (response.status === SubscriptionStatusEnum.FAILED) {
				setShoppingCartRenewal(
					response.last_payment_summary
						.shopping_cart as unknown as ShoppingCart,
				);
				setShowSelectCard(true);
				setSubscriptionWithPaymentInfo(response);
			}
		} catch (error) {
			const errorResponse = error as JsonObject;
			const message = errorResponse?.response?.data?.resource?.message;
			showAlertTop({
				title: 'Error',
				type: ALERT_TYPE.ERROR,
				description: message || errorResponse?.message,
			});
		} finally {
			setIsLoading(false);
		}
	};

	useEffect(() => {
		if (subscriptionId && showModal) {
			executeSubscriptionWithPaymentInfo();
		}
	}, [subscriptionId, showModal]);

	// Reset state when modal opens
	useEffect(() => {
		if (showModal) {
			setShowSelectCard(false);
			setShowModalPaymentFailed(false);
			setSelectedCard(null);
		}
	}, [showModal]);

	const getErrorContent = () => {
		const failureReason = subscriptionWithPaymentInfo?.last_payment_summary?.transaction?.failure_reason;
		const specificError = failureReason ? getSpecificErrorMessage(failureReason) : null;

		if (specificError) {
			return (
				<div className='mb-6 text-s w-11/12 font-light'>
					<p className='text-gray-600 mb-4'>
						El cargo a tu tarjeta{' '}
						<b>
							{selectedCard?.details?.brand} ****
							{selectedCard?.details?.last_four}
						</b>{' '}
						no fue autorizado:
					</p>
					<p className='text-gray-700 font-medium bg-gray-50 p-3 rounded-md'>
						{specificError}
					</p>
				</div>
			);
		}

		// Generic error list fallback
		return (
			<div className='mb-6 text-s w-11/12 font-light'>
				<p className='text-gray-600 mb-4'>
					El cargo a tu tarjeta{' '}
					<b>
						{selectedCard?.details?.brand} ****
						{selectedCard?.details?.last_four}
					</b>{' '}
					no fue autorizado. Esto puede deberse a:
				</p>
				<ul className='list-disc pl-6 text-gray-600'>
					<li>Necesitas aprobar el cargo en la app de tu banco.</li>
					<li>No hay fondos suficientes en tu cuenta.</li>
					<li>La tarjeta está vencida o bloqueada.</li>
				</ul>
			</div>
		);
	};

	return (
		<Modal
			open={showModal}
			onClose={handleCloseModal}
			sx='w-[70%] !max-w-[440px]'
			showCloseButton={true}
		>
			{isLoading && (
				<div className='flex flex-row items-center justify-center m-2 w-full'>
					<CircularProgress />
				</div>
			)}
			{showModalPaymentFailed && (
				<div className='p-4'>
					<div className='flex justify-center items-center gap-2 mb-4 '>
						<Barricade
							size={70}
							color='#e1ae4b'
						/>
					</div>
					<div className=' flex justify-center items-center'>
						<p className='text-base text-center mt-2'>
							<b>No pudimos procesar el pago de tu renovación</b>
						</p>
					</div>
					<br />
					<div className='flex items-center justify-center w-full'>
						{getErrorContent()}
					</div>
					<hr />

					<div className='flex justify-between items-center gap-2 font-ligth mt-4'>
						<Text
							size='m'
							weight='bold'
						>
							Total:
						</Text>

						<Text
							size='m'
							weight='bold'
						>
							${formatPrice(shoppingCartRenewal?.total)}
						</Text>
					</div>
					<br />
					<Button
						variant='primary'
						label='Cambiar Metodo de Pago'
						sx='!w-[90%] mx-auto'
						onClick={() => {
							setShowSelectCard(true);
							setShowModalPaymentFailed(false);
						}}
						loading={isLoadingButton}
					/>
				</div>
			)}

			{showSelectCard && subscriptionWithPaymentInfo && (
				<ModalSelectCard
					shoppingCartId={shoppingCartRenewal?.id as number}
					amount={shoppingCartRenewal?.total as number}
					subscription={subscriptionWithPaymentInfo}
					showModal={showSelectCard}
					setShowModal={(value: boolean) => {
						setShowSelectCard(value);
						// Only close the main modal if the user explicitly cancels
						// Don't close on payment success - let our handlers manage that
						if (!value && !showModalPaymentFailed) {
							setShowModal(value);
						}
					}}
					onPaymentSuccess={(token: Token) => {
						// Call our success handler first
						onPaymentSuccess(token);
						// Don't let the modal close automatically
					}}
					onPaymentFailed={(token: Token) => {
						onPaymentFailed(token);
					}}
					preventAutoModalClose={true}
				/>
			)}
		</Modal>
	);
};

export default ModalFailedRenewal;
